import { type Cast, type Crew, type Maybe } from '@tmdb-graphql-api/schema/types';
import { ImageSize, ProfileSize } from '#enums/imageSizes.ts';
import { SectionType } from '#enums/mediaTypes.ts';
import { buildImagePath } from '#helpers/buildImagePath.ts';
import { get } from '#helpers/otherHelpers.ts';
import { type Config } from '#types.ts';
import { type TileItem } from '../../../Tile/types.ts';
import {
  type EpisodeCastAndCrewData,
  type MovieCastAndCrewData,
  type SeasonCastAndCrewData,
  type TvCastAndCrewData,
} from '../../types.ts';

const getSubTitle = (credit: Cast | Crew) => {
  if ('character' in credit && credit.character) {
    return `as ${credit.character}`;
  }

  if ('job' in credit && credit.job) {
    return credit.job;
  }

  return;
};

const format = (credits: Maybe<Cast | Crew>[], { images }: Config) =>
  credits.reduce((acc: TileItem[], credit) => {
    if (!credit) {
      return acc;
    }

    const match = acc.find(entry => entry.title === credit.name);

    if (match) {
      const subTitle = getSubTitle(credit);
      match.subTitle = [match.subTitle, subTitle].filter(entry => !!entry).join(' / ');
      return acc;
    }

    const { id, name, profilePath } = credit;

    acc.push({
      id,
      src: buildImagePath(profilePath, ImageSize.Profile, ProfileSize.MD, images),
      subTitle: getSubTitle(credit),
      title: name,
      type: SectionType.Person,
    });

    return acc;
  }, []);

export const formatCarouselItems = (
  data: EpisodeCastAndCrewData | MovieCastAndCrewData | SeasonCastAndCrewData | TvCastAndCrewData | null | undefined,
  subType: 'cast' | 'crew' | 'guestStars',
  config: Config,
) => {
  if (!data) {
    return [];
  }

  if ('episode' in data) {
    return format(get<Maybe<Cast | Crew>[]>(data.episode, subType, []), config);
  }

  if ('movie' in data) {
    return format(get<Maybe<Cast | Crew>[]>(data.movie, subType, []), config);
  }

  if ('season' in data) {
    return format(get<Maybe<Cast | Crew>[]>(data.season, subType, []), config);
  }

  return format(get<Maybe<Cast | Crew>[]>(data.tv, subType, []), config);
};
